import React, { useEffect, useState } from 'react';

import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { practiceAreasService } from '../../../services/practiceAreasService';

const PracticeAreasGrid = () => {
	const [selectedArea, setSelectedArea] = useState(null);
	const [practiceAreas, setPracticeAreas] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	// Function to convert text with \n\n* into bullet points
	const formatDescription = (text) => {
		if (!text) return '';

		// Split by \n\n* and convert to bullet points
		const parts = text.split(/\n\n\*/);
		if (parts.length === 1) return text; // No bullet points found

		const firstPart = parts[0];
		const bulletPoints = parts
			.slice(1)
			.map((point) => point.trim())
			.filter((point) => point);

		return (
			<div>
				{firstPart && <p className='mb-4'>{firstPart}</p>}
				{bulletPoints.length > 0 && (
					<ul className='list-disc list-inside space-y-2'>
						{bulletPoints.map((point, index) => (
							<li key={index} className='text-text-primary'>
								{point}
							</li>
						))}
					</ul>
				)}
			</div>
		);
	};

	// Load practice areas from database
	useEffect(() => {
		const loadPracticeAreas = async () => {
			try {
				setLoading(true);
				const { data, error } = await practiceAreasService.getFeaturedPracticeAreas();

				if (error) {
					setError(error);
					return;
				}

				setPracticeAreas(data || []);
			} catch (err) {
				setError('Failed to load practice areas');
				console.error('Error loading practice areas:', err);
			} finally {
				setLoading(false);
			}
		};

		loadPracticeAreas();
	}, []);

	const openModal = (area) => {
		setSelectedArea(area);
		document.body.style.overflow = 'hidden';
	};

	const closeModal = () => {
		setSelectedArea(null);
		document.body.style.overflow = 'unset';
	};

	// Loading state
	if (loading) {
		return (
			<section className='py-20 bg-gradient-to-br from-gray-50 via-white to-gray-100'>
				<div className='container mx-auto px-4'>
					<div className='text-center'>
						<div className='animate-spin rounded-full h-12 w-12 border-b-2 border-[#800020] mx-auto mb-4'></div>
						<p className='text-gray-600'>Loading practice areas...</p>
					</div>
				</div>
			</section>
		);
	}

	// Error state
	if (error || practiceAreas.length === 0) {
		return (
			<section className='py-20 bg-gradient-to-br from-gray-50 via-white to-gray-100'>
				<div className='container mx-auto px-4'>
					<div className='text-center'>
						<Icon name='AlertCircle' size={48} className='mx-auto mb-4 text-red-500' />
						<h3 className='text-gray-900 text-xl mb-2'>Error Loading Practice Areas</h3>
						<p className='text-gray-600'>{error?.message || 'No practice areas available'}</p>
					</div>
				</div>
			</section>
		);
	}

	return (
		<>
			<section className='py-20 bg-gradient-to-br from-gray-50 via-white to-gray-100 relative overflow-hidden'>
				{/* Background Pattern */}
				<div className='absolute inset-0 opacity-5'>
					<div className='absolute top-0 left-0 w-full h-full bg-gradient-to-r from-primary/10 via-transparent to-accent/10'></div>
					<div className='absolute top-20 right-10 w-32 h-32 bg-primary/5 rounded-full blur-3xl'></div>
					<div className='absolute bottom-20 left-10 w-40 h-40 bg-accent/5 rounded-full blur-3xl'></div>
				</div>

				<div className='max-w-7xl mx-auto px-4 lg:px-8 relative z-10'>
					<div className='text-center mb-16'>
						<motion.div
							initial={{ y: 30, opacity: 0 }}
							whileInView={{ y: 0, opacity: 1 }}
							transition={{ duration: 0.8 }}
							viewport={{ once: true }}
						>
							<h2 className='font-playfair font-bold text-4xl lg:text-5xl text-primary mb-6'>
								Our Expertise
							</h2>
							<p className='font-source text-xl text-text-secondary max-w-3xl mx-auto'>
								Our diverse practice areas ensure we can handle all your legal needs with the
								expertise and attention you deserve.
							</p>
						</motion.div>
					</div>

					<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'>
						{practiceAreas.map((area, index) => {
							// Define colors for different practice areas
							const getIconColor = (title) => {
								const colors = {
									Aviation: 'bg-red-600',
									'Energy and Natural Resources': 'bg-blue-600',
									'Criminal Defense': 'bg-red-500',
									'Family Law': 'bg-yellow-500',
									'Real Estate Law': 'bg-green-600',
									'Employment Law': 'bg-orange-600',
									'Banking and Finance': 'bg-indigo-600',
									'Commercial Litigation': 'bg-purple-600',
									'Corporate Law': 'bg-teal-600',
									Environmental: 'bg-emerald-600',
									'Foreign Investment': 'bg-cyan-600',
									'Intellectual Property': 'bg-pink-600',
									'Labour/Industrial and Employment': 'bg-amber-600',
									'Medical/Healthcare': 'bg-rose-600',
									'Probate Practice': 'bg-violet-600',
									Sports: 'bg-lime-600',
									Tax: 'bg-slate-600',
									'Real Estate/Property': 'bg-stone-600',
								};
								return colors[title] || 'bg-[#800020]';
							};

							return (
								<motion.div
									key={area.id}
									initial={{ y: 30, opacity: 0 }}
									whileInView={{ y: 0, opacity: 1 }}
									transition={{ duration: 0.6, delay: index * 0.1 }}
									viewport={{ once: true }}
									className='group cursor-pointer'
									onClick={() => openModal(area)}
								>
									<div className='bg-white rounded-lg p-8 h-full shadow-lg hover:shadow-xl transition-shadow duration-300 min-h-[280px]'>
										<div
											className={`w-18 h-18 ${getIconColor(
												area.title
											)} rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}
										>
											<Icon name={area.icon} size={32} color='white' strokeWidth={2} />
										</div>

										<h3 className='font-playfair font-semibold text-2xl text-primary mb-4 group-hover:text-accent transition-colors duration-300'>
											{area.title}
										</h3>

										<p className='font-source text-text-secondary leading-relaxed mb-6 text-base'>
											{area.description}
										</p>

										<div className='flex items-center justify-between'>
											<span className='font-source text-sm font-medium text-accent'>
												{area.tagline || 'Expert Legal Services'}
											</span>
											<Icon
												name='ArrowRight'
												size={20}
												className='text-primary group-hover:translate-x-1 transition-transform duration-300'
											/>
										</div>
									</div>
								</motion.div>
							);
						})}
					</div>

					<div className='text-center mt-12'>
						<Link to='/practice-areas'>
							<Button
								variant='outline'
								className='border-2 border-primary text-primary hover:bg-primary hover:text-white font-source font-semibold px-8 py-3'
								iconName='Plus'
								iconPosition='left'
								iconSize={20}
							>
								View All Practice Areas
							</Button>
						</Link>
					</div>
				</div>
			</section>

			{/* Modal */}
			{selectedArea && (
				<div className='fixed inset-0 z-50 modal-backdrop flex items-center justify-center p-4'>
					<motion.div
						initial={{ scale: 0.95, opacity: 0 }}
						animate={{ scale: 1, opacity: 1 }}
						exit={{ scale: 0.95, opacity: 0 }}
						transition={{ duration: 0.3 }}
						className='bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto modal-enter'
					>
						<div className='p-8'>
							<div className='flex items-start justify-between mb-6'>
								<div className='flex items-center space-x-4'>
									<div className='w-16 h-16 bg-[#800020] rounded-lg flex items-center justify-center'>
										<Icon name={selectedArea.icon} size={28} color='white' strokeWidth={2} />
									</div>
									<div>
										<h3 className='font-playfair font-bold text-2xl text-primary'>
											{selectedArea.title}
										</h3>
										<p className='font-source text-accent font-medium'>
											{selectedArea.tagline || 'Expert Legal Services'}
										</p>
									</div>
								</div>
								<button
									onClick={closeModal}
									className='p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200'
								>
									<Icon name='X' size={24} className='text-text-secondary' />
								</button>
							</div>

							<div className='space-y-6'>
								<p className='font-source text-lg text-text-secondary leading-relaxed'>
									{selectedArea.description}
								</p>

								<div className='font-source text-text-primary leading-relaxed'>
									{formatDescription(selectedArea.full_description || selectedArea.description)}
								</div>

								<div className='flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-100'>
									<a
										href='/contact'
										className='inline-flex items-center px-6 py-3 bg-cta hover:bg-cta/90 text-cta-foreground font-source font-semibold rounded-lg transition-all duration-300'
									>
										<Icon name='Calendar' size={18} className='mr-2' />
										Schedule a Consultation
									</a>
									<Button
										variant='outline'
										className='border-primary text-primary hover:bg-primary hover:text-white font-source font-semibold'
										iconName='Phone'
										iconPosition='left'
										iconSize={18}
									>
										Call Now
									</Button>
								</div>
							</div>
						</div>
					</motion.div>
				</div>
			)}
		</>
	);
};

export default PracticeAreasGrid;
